import React, { Fragment } from 'react';
import { Route, Switch } from 'react-router-dom';

import ErrorPage from './containers/ErrorPage';
import { Helmet } from 'react-helmet';
import LoginPage from './containers/LoginPage';
import PageLoader from './PageLoader';
import PageNotFound from './containers/PageNotFound';
import ResetPasswordPage from './containers/ResetPasswordPage';
import UIContextProvider from './UIContextProvider';
import asyncComponent from './lib/asyncComponent';
import constants from './constants';

const {
  BOOKINGS_URL,
  BOOKING_URL,
  BOOKING_ACCOMMODATION_URL,
  BOOKING_EXTRAS_URL,
  BOOKING_RESALE_URL,
  BOOKING_UPDATE_DELIVERY_URL,
  BOOKING_PAYMENT_PLAN_VIEW_URL,
  BOOKING_PAYMENT_PLAN_CHANGE_PAYMENT_URL,
  BOOKING_PAYMENT_PLAN_CHANGE_DAY_URL,
  BOOKING_TICKETS_URL,
  BOOKING_REQUIRED_DATA_URL,
  BOOKING_REQUIRED_DATA_ADDRESS_URL,
  BOOKING_MAKE_PAYMENT_URL,
  BOOKING_PAY_IN_FULL_URL,
  CART_URL,
  CHECKOUT_URL,
  PROFILE_URL,
  PROFILE_DETAILS_BANK_ACCOUNT,
  PROFILE_DETAILS_PASSPORT,
  SINGLE_ACCOMMODATION_URL,
  LOGIN_URL,
  ERROR_URL,
  RESET_PASSWORD_URL,
  CHANGE_PASSWORD_URL,
  PAGE_TITLE_SUFFIX,
  BOOKING_TRANSPORT_URL,
  BOOKING_TICKET_DETAILS_URL,
} = constants;

const getPageTitle = title => `${title} - ${PAGE_TITLE_SUFFIX}`;

const renderWithTitle = (title, Page) => (
  <Fragment>
    <Helmet title={getPageTitle(title)} />
    <Page />
  </Fragment>
);

const generalRoutesMapper = [
  {
    path: BOOKINGS_URL,
    title: getPageTitle('Manage My Bookings'),
    component: asyncComponent(() => import('./containers/BookingsPage')),
  },
  {
    path: PROFILE_URL,
    title: getPageTitle('Profile'),
    component: asyncComponent(() => import('./containers/ProfilePage')),
  },
  {
    path: PROFILE_DETAILS_BANK_ACCOUNT,
    title: getPageTitle('Update Bank Account'),
    component: asyncComponent(() =>
      import('./containers/ProfileBankAccountPage'),
    ),
  },
  {
    path: PROFILE_DETAILS_PASSPORT,
    title: getPageTitle('Update Passport'),
    component: asyncComponent(() => import('./containers/ProfilePassportPage')),
  },
  {
    path: CHANGE_PASSWORD_URL,
    title: getPageTitle('Update Password'),
    component: asyncComponent(() => import('./containers/ChangePasswordPage')),
  },
];

const bookingRoutesMapper = [
  {
    path: BOOKING_URL,
    title: getPageTitle('My Booking'),
    component: asyncComponent(() => import('./containers/BookingPage')),
  },
  {
    path: BOOKING_EXTRAS_URL,
    title: getPageTitle('Booking Extras'),
    component: asyncComponent(() => import('./containers/BookingExtrasPage')),
  },
  {
    path: BOOKING_ACCOMMODATION_URL,
    title: getPageTitle('Accommodation'),
    component: asyncComponent(() => import('./containers/AccommodationPage')),
  },
  {
    path: BOOKING_TRANSPORT_URL,
    title: getPageTitle('Transport'),
    component: asyncComponent(() => import('./containers/TransportPage')),
  },
  {
    path: BOOKING_TICKET_DETAILS_URL,
    title: getPageTitle('Ticket Details'),
    component: asyncComponent(() => import('./containers/TicketDetailsPage')),
  },
  {
    path: BOOKING_RESALE_URL,
    title: getPageTitle('Booking Resale'),
    component: asyncComponent(() => import('./containers/BookingResalePage')),
  },
  {
    path: BOOKING_PAYMENT_PLAN_VIEW_URL,
    title: getPageTitle('Payment Plan'),
    component: asyncComponent(() => import('./containers/PaymentPlanPage')),
  },
  {
    path: BOOKING_PAYMENT_PLAN_CHANGE_PAYMENT_URL,
    title: getPageTitle('Change Payment Details'),
    component: asyncComponent(() =>
      import('./containers/PaymentPlanChangePaymentPage'),
    ),
  },
  {
    path: BOOKING_PAYMENT_PLAN_CHANGE_DAY_URL,
    title: getPageTitle('Change Payment Day'),
    component: asyncComponent(() =>
      import('./containers/PaymentPlanChangeDayPage'),
    ),
  },
  {
    path: BOOKING_REQUIRED_DATA_URL,
    title: getPageTitle('Required Data'),
    component: asyncComponent(() =>
      import('./containers/BookingRequiredDataPage'),
    ),
  },
  {
    path: BOOKING_REQUIRED_DATA_ADDRESS_URL,
    title: getPageTitle('Required Address'),
    component: asyncComponent(() =>
      import('./containers/BookingRequiredDataAddressPage'),
    ),
  },
  {
    path: BOOKING_MAKE_PAYMENT_URL,
    title: getPageTitle('Make a Payment'),
    component: asyncComponent(() => import('./containers/MakePaymentPage')),
  },
  {
    path: BOOKING_PAY_IN_FULL_URL,
    title: getPageTitle('Pay in Full'),
    component: asyncComponent(() => import('./containers/PayInFullPage')),
  },
  {
    path: BOOKING_TICKETS_URL,
    title: getPageTitle('Tickets'),
    component: asyncComponent(() => import('./containers/TicketsPage')),
  },
  {
    path: BOOKING_UPDATE_DELIVERY_URL,
    title: getPageTitle('Update Delivery Address'),
    component: asyncComponent(() => import('./containers/BookingAddressPage')),
  },
  {
    path: CART_URL,
    title: getPageTitle('Your Basket'),
    component: asyncComponent(() => import('./containers/CartPage')),
  },
  {
    path: CHECKOUT_URL,
    title: getPageTitle('Checkout'),
    component: asyncComponent(() => import('./containers/CheckoutPage')),
  },
  {
    path: SINGLE_ACCOMMODATION_URL,
    component: asyncComponent(() =>
      import('./containers/SingleAccommodationPage'),
    ),
  },
];

const Routes = () => (
  <Switch>
    {generalRoutesMapper.map(route => (
      <Route
        key={route.path}
        exact
        path={route.path}
        render={() => (
          <PageLoader
            title={route.title}
            component={route.component}
            ui={route.ui}
          />
        )}
      />
    ))}

    <Route path="/booking/:booking_reference">
      <UIContextProvider>
        {bookingRoutesMapper.map(route => (
          <Route
            key={route.path}
            exact
            path={route.path}
            render={() => (
              <PageLoader
                title={route.title}
                component={route.component}
                ui={route.ui}
              />
            )}
          />
        ))}
      </UIContextProvider>
    </Route>

    <Route
      exact
      path={LOGIN_URL}
      render={() => renderWithTitle('Login', LoginPage)}
    />
    <Route
      exact
      path={RESET_PASSWORD_URL}
      render={() => renderWithTitle('Password Reset', ResetPasswordPage)}
    />
    <Route
      path={ERROR_URL}
      render={() => renderWithTitle('Error', ErrorPage)}
    />
    <Route render={() => renderWithTitle('Page Not Found', PageNotFound)} />
  </Switch>
);

export default Routes;
